import { Link } from "react-router-dom";
import { ArrowRight, Globe, ShoppingBag, Search, Fingerprint, Megaphone, Smartphone } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Motion";

const SERVICES = [
  {
    icon: Globe, title: "Website Design", span: "md:col-span-3",
    desc: "Custom-designed, hand-coded websites that load fast, look stunning and turn visitors into paying customers. No bloated templates — every pixel earns its place.",
    points: ["Custom design, zero templates", "Mobile-first & responsive", "Built to convert"],
  },
  {
    icon: ShoppingBag, title: "E-commerce", span: "md:col-span-3",
    desc: "Online stores engineered for sales. Smooth product pages, painless checkout and the integrations your business already uses.",
    points: ["Shopify & custom builds", "Conversion-focused checkout", "Payments & shipping setup"],
  },
  {
    icon: Search, title: "SEO", span: "md:col-span-2",
    desc: "Show up when customers search. Technical SEO, local search and content structure that put you on the map — literally.",
    points: ["Local Alberta SEO", "Technical audits", "Google Business setup"],
  },
  {
    icon: Fingerprint, title: "Branding", span: "md:col-span-2",
    desc: "Logos, colours and voice that make your business unforgettable. A brand system you can actually use everywhere.",
    points: ["Logo & identity", "Brand guidelines", "Social kits"],
  },
  {
    icon: Megaphone, title: "Digital Marketing", span: "md:col-span-2",
    desc: "Campaigns that bring the right people to your site — and analytics that show exactly what's working.",
    points: ["Google & Meta ads", "Email campaigns", "Analytics & reporting"],
  },
  {
    icon: Smartphone, title: "Web & Mobile Apps", span: "md:col-span-6",
    desc: "When a website isn't enough, we build the tool. Booking systems, dashboards, customer portals and mobile apps — designed and developed end-to-end by the same small team, so nothing gets lost in translation.",
    points: ["Custom web applications", "iOS & Android apps", "Dashboards & portals", "Ongoing support"],
  },
];

const Services = () => (
  <div className="pt-40 pb-24" data-testid="services-page">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Overline>Services</Overline>
      <MaskedLines
        as="h1"
        className="mt-8 font-sora font-extrabold tracking-tighter leading-[1.0] text-4xl sm:text-6xl lg:text-7xl"
        lines={["Six ways we make", "your business impossible", "to ignore."]}
      />
      <Reveal delay={0.3}>
        <p className="mt-8 max-w-xl text-white/60 leading-relaxed">
          One studio, everything covered. Pick a single service or hand us the whole
          digital puzzle — either way you get senior-level work without agency bloat.
        </p>
      </Reveal>

      <div className="mt-20 grid md:grid-cols-6 gap-6" data-testid="services-grid">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.1} className={s.span}>
            <div
              data-testid={`service-card-${s.title.toLowerCase().replace(/[\s&]+/g, "-")}`}
              className="glow-border group rounded-2xl border border-white/10 bg-surface p-10 h-full transition-transform duration-500 hover:-translate-y-1.5"
            >
              <div className="flex items-start justify-between">
                <s.icon className="w-9 h-9 text-brand-cyan" strokeWidth={1.4} />
                <span className="font-sora text-sm font-bold text-white/20">0{i + 1}</span>
              </div>
              <h2 className="font-sora font-bold text-2xl md:text-3xl mt-8 tracking-tight">{s.title}</h2>
              <p className="text-sm md:text-base text-white/55 mt-4 leading-relaxed max-w-xl">{s.desc}</p>
              <ul className="mt-8 flex flex-wrap gap-2">
                {s.points.map((p) => (
                  <li key={p} className="text-xs border border-white/15 rounded-full px-3 py-1.5 text-white/60">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-28">
        <div className="rounded-3xl border border-white/10 bg-surface overflow-hidden" data-testid="services-menu">
          <div className="text-center pt-16 pb-12 border-b border-white/10 px-8">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold">For restaurants, cafés & local brands</p>
            <h2 className="font-sora font-extrabold tracking-tighter text-4xl md:text-6xl mt-5">The Menu</h2>
            <p className="text-white/45 text-sm mt-4 italic">Served fresh, in courses. No hidden charges.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-x-20 gap-y-12 p-10 md:p-20">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="group" data-testid={`menu-item-${i}`}>
                <div className="flex items-baseline gap-3">
                  <span className="font-sora text-xs text-brand-cyan font-bold shrink-0">0{i + 1}</span>
                  <h3 className="font-sora font-bold text-xl md:text-2xl tracking-tight transition-colors duration-300 group-hover:text-brand-cyan">
                    {s.title}
                  </h3>
                  <span className="flex-1 border-b border-dotted border-white/25 -translate-y-1.5" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/35 shrink-0">
                    {["House special", "Chef's pick", "Signature", "Classic", "Seasonal", "Crowd favourite"][i]}
                  </span>
                </div>
                <p className="text-sm text-white/50 mt-3 leading-relaxed pl-7">{s.points.join(" · ")}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 px-10 md:px-20 py-10 flex flex-wrap items-center justify-between gap-6">
            <p className="text-white/50 text-sm italic">Everything is made to order — tell us your appetite and budget.</p>
            <Link
              to="/contact"
              data-testid="menu-cta-quote"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-brand-cyan hover:text-white transition-colors duration-300"
            >
              Place your order
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-24">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-brand-blue/10 to-brand-purple/10 p-10 md:p-16 flex flex-wrap items-center justify-between gap-8">
          <div>
            <h2 className="font-sora font-extrabold tracking-tighter text-3xl md:text-4xl">Not sure what you need?</h2>
            <p className="text-white/60 mt-3 max-w-md">Tell us where your business is hurting. We'll recommend the smallest thing that makes the biggest difference.</p>
          </div>
          <Link
            to="/contact"
            data-testid="services-cta-quote"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-8 py-4 font-semibold transition-transform duration-300 hover:scale-[1.04]"
          >
            Get a Free Quote
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </div>
  </div>
);

export default Services;
